import { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import Header from '../../components/Header';
import { getQuoteBook, QuoteBookEntry } from '../../lib/storage/quoteBook';
import { getMdBackendSettings } from '../../lib/marketDataBackend';
import PanelPlaceholder from '../../components/ui/PanelPlaceholder';

type DataSourceFilter = 'all' | 'quantra' | 'my';
type TransformMode = 'raw' | 'rebase100' | 'pctChange' | 'zscore';
type TimeRangePreset = '1m' | '3m' | '6m' | 'ytd' | '1y' | '3y' | '5y' | 'all';
type AxisSide = 'left' | 'right';

interface PlotSeries {
  id: string;
  label: string;
  kind: string;
  currency?: string;
  source: 'Quantra Data' | 'My Data';
  points: Array<{ ts: number; value: number }>;
}

interface ZoomWindow {
  startPct: number;
  endPct: number;
}

interface RemoteWindowCacheEntry {
  key: string;
  points: Array<{ ts: number; value: number }>;
}

function inferSource(entry: QuoteBookEntry): 'Quantra Data' | 'My Data' {
  const description = (entry.description || '').toLowerCase();
  const looksCanonical = entry.id.split('.').length >= 5;
  if (description.includes('market data backend') || looksCanonical) return 'Quantra Data';
  return 'My Data';
}

function toChartSeries(entry: QuoteBookEntry): PlotSeries | null {
  const points = (entry.series || [])
    .filter((p) => typeof p.value === 'number' && Number.isFinite(p.value))
    .map((p) => {
      const ts = Date.parse(`${p.date}T00:00:00Z`);
      return { ts, value: p.value };
    })
    .filter((p) => Number.isFinite(p.ts))
    .sort((a, b) => a.ts - b.ts);

  if (points.length < 2) return null;

  return {
    id: entry.id,
    label: entry.label || entry.id,
    kind: entry.kind,
    currency: entry.currency,
    source: inferSource(entry),
    points,
  };
}

function cutoffTimestamp(preset: TimeRangePreset, maxTs: number): number | null {
  if (preset === 'all') return null;
  const d = new Date(maxTs);
  if (preset === 'ytd') {
    return Date.UTC(d.getUTCFullYear(), 0, 1);
  }

  if (preset === '1m') d.setUTCMonth(d.getUTCMonth() - 1);
  if (preset === '3m') d.setUTCMonth(d.getUTCMonth() - 3);
  if (preset === '6m') d.setUTCMonth(d.getUTCMonth() - 6);
  if (preset === '1y') d.setUTCFullYear(d.getUTCFullYear() - 1);
  if (preset === '3y') d.setUTCFullYear(d.getUTCFullYear() - 3);
  if (preset === '5y') d.setUTCFullYear(d.getUTCFullYear() - 5);
  return d.getTime();
}

function toDateInputValue(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

function parseDateInput(value: string): number | null {
  if (!value) return null;
  const ts = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(ts) ? ts : null;
}

function transformedValues(values: number[], mode: TransformMode): number[] {
  if (values.length === 0) return values;
  if (mode === 'raw') return values;

  const first = values[0];
  if (mode === 'rebase100') {
    if (first === 0) return values.map(() => NaN);
    return values.map((v) => (v / first) * 100);
  }
  if (mode === 'pctChange') {
    if (first === 0) return values.map(() => NaN);
    return values.map((v) => ((v / first) - 1) * 100);
  }

  const mean = values.reduce((acc, v) => acc + v, 0) / values.length;
  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / values.length;
  const std = Math.sqrt(variance);
  if (std === 0) return values.map(() => 0);
  return values.map((v) => (v - mean) / std);
}

function defaultAxisSide(kind: string): AxisSide {
  return kind === 'Rate' || kind === 'Spread' ? 'left' : 'right';
}

function downsampleMinMax(
  points: Array<{ ts: number; value: number }>,
  maxPoints: number
): Array<{ ts: number; value: number }> {
  if (points.length <= maxPoints || maxPoints < 8) return points;

  const bucketCount = Math.max(1, Math.floor((maxPoints - 2) / 2));
  const first = points[0];
  const last = points[points.length - 1];
  const interior = points.slice(1, -1);
  const bucketSize = Math.ceil(interior.length / bucketCount);
  const out: Array<{ ts: number; value: number }> = [first];

  for (let i = 0; i < interior.length; i += bucketSize) {
    const bucket = interior.slice(i, i + bucketSize);
    if (bucket.length === 0) continue;
    let min = bucket[0];
    let max = bucket[0];
    for (const p of bucket) {
      if (p.value < min.value) min = p;
      if (p.value > max.value) max = p;
    }
    if (min.ts <= max.ts) {
      out.push(min);
      if (max.ts !== min.ts) out.push(max);
    } else {
      out.push(max);
      if (max.ts !== min.ts) out.push(min);
    }
  }

  out.push(last);
  return out.sort((a, b) => a.ts - b.ts);
}

function zoomTimeBounds(
  points: Array<{ ts: number; value: number }>,
  startPct: number,
  endPct: number
): { startTs: number; endTs: number } | null {
  if (points.length === 0) return null;
  if (points.length === 1) {
    const only = points[0].ts;
    return { startTs: only, endTs: only };
  }
  const clampedStart = Math.max(0, Math.min(100, startPct));
  const clampedEnd = Math.max(clampedStart, Math.min(100, endPct));
  const minTs = points[0].ts;
  const maxTs = points[points.length - 1].ts;
  const span = Math.max(1, maxTs - minTs);
  const startTs = minTs + (clampedStart / 100) * span;
  const endTs = minTs + (clampedEnd / 100) * span;
  return { startTs, endTs };
}

function sliceByTimeBounds(
  points: Array<{ ts: number; value: number }>,
  startTs: number,
  endTs: number
): Array<{ ts: number; value: number }> {
  if (points.length === 0) return points;
  return points.filter((p) => p.ts >= startTs && p.ts <= endTs);
}

function isFullZoom(zoom: ZoomWindow): boolean {
  return zoom.startPct <= 0.01 && zoom.endPct >= 99.99;
}

function buildWindowKey(
  rangePreset: TimeRangePreset,
  zoom: ZoomWindow,
  rangeStartTs: number,
  rangeEndTs: number,
  bounds: { startTs: number; endTs: number } | null
): string {
  const rangePart = `${Math.floor(rangeStartTs)}:${Math.floor(rangeEndTs)}`;
  if (isFullZoom(zoom)) return `${rangePreset}|full|${rangePart}`;
  if (!bounds) return `${rangePreset}|full|${rangePart}`;
  return `${rangePreset}|${Math.floor(bounds.startTs)}:${Math.floor(bounds.endTs)}|${rangePart}`;
}

export default function TimeSeriesLab() {
  const [allSeries, setAllSeries] = useState<PlotSeries[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<DataSourceFilter>('all');
  const [transformMode, setTransformMode] = useState<TransformMode>('raw');
  const [rangePreset, setRangePreset] = useState<TimeRangePreset>('1y');
  const [minDateInput, setMinDateInput] = useState('');
  const [maxDateInput, setMaxDateInput] = useState('');
  const [useLogScale, setUseLogScale] = useState(false);
  const [axisById, setAxisById] = useState<Record<string, AxisSide>>({});
  const [zoomWindow, setZoomWindow] = useState<ZoomWindow>({ startPct: 0, endPct: 100 });
  const [remoteWindowById, setRemoteWindowById] = useState<Record<string, RemoteWindowCacheEntry>>({});
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [fetchedWindowKeys, setFetchedWindowKeys] = useState<Record<string, string>>({});
  const FIXED_POINTS_PER_VIEW = 400;

  useEffect(() => {
    const entries = getQuoteBook();
    const mapped = entries.map(toChartSeries).filter((x): x is PlotSeries => x !== null);
    setAllSeries(mapped);
    setSelectedIds(mapped.slice(0, 8).map((s) => s.id));
  }, []);

  const filteredUniverse = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allSeries.filter((s) => {
      if (sourceFilter === 'quantra' && s.source !== 'Quantra Data') return false;
      if (sourceFilter === 'my' && s.source !== 'My Data') return false;
      if (!q) return true;
      return (
        s.id.toLowerCase().includes(q) ||
        s.label.toLowerCase().includes(q) ||
        s.kind.toLowerCase().includes(q) ||
        (s.currency || '').toLowerCase().includes(q)
      );
    });
  }, [allSeries, search, sourceFilter]);

  const selectedSeries = useMemo(
    () => filteredUniverse.filter((s) => selectedIds.includes(s.id)),
    [filteredUniverse, selectedIds]
  );

  const maxTs = useMemo(() => {
    let m = 0;
    for (const s of selectedSeries) {
      const last = s.points[s.points.length - 1]?.ts || 0;
      if (last > m) m = last;
    }
    return m;
  }, [selectedSeries]);

  const minTs = useMemo(() => {
    let m = Number.POSITIVE_INFINITY;
    for (const s of selectedSeries) {
      const first = s.points[0]?.ts;
      if (typeof first === 'number' && first < m) m = first;
    }
    return Number.isFinite(m) ? m : 0;
  }, [selectedSeries]);

  useEffect(() => {
    if (maxTs === 0) return;
    const presetStart = rangePreset === 'all'
      ? Date.parse('1900-01-01T00:00:00.000Z')
      : (cutoffTimestamp(rangePreset, maxTs) ?? minTs);
    const boundedStart = rangePreset === 'all'
      ? presetStart
      : Math.max(minTs || presetStart, presetStart);
    setMinDateInput(toDateInputValue(boundedStart));
    setMaxDateInput(toDateInputValue(maxTs));
  }, [rangePreset, maxTs, minTs]);

  const chartSeries = useMemo(() => {
    if (selectedSeries.length === 0 || maxTs === 0) return [];
    const parsedMinTs = parseDateInput(minDateInput);
    const parsedMaxTs = parseDateInput(maxDateInput);
    const fallbackMin = rangePreset === 'all'
      ? Date.parse('1900-01-01T00:00:00.000Z')
      : (cutoffTimestamp(rangePreset, maxTs) ?? minTs);
    const rangeStartTs = parsedMinTs ?? fallbackMin;
    const rangeEndTs = parsedMaxTs ?? maxTs;

    return selectedSeries
      .map((s) => {
        const slicedRaw = s.points.filter((p) => p.ts >= rangeStartTs && p.ts <= rangeEndTs);
        const bounds = zoomTimeBounds(slicedRaw, zoomWindow.startPct, zoomWindow.endPct);
        const windowedRaw = bounds ? sliceByTimeBounds(slicedRaw, bounds.startTs, bounds.endTs) : slicedRaw;
        const windowKey = buildWindowKey(rangePreset, zoomWindow, rangeStartTs, rangeEndTs, bounds);
        const remoteEntry = remoteWindowById[s.id];
        const remoteWindow = remoteEntry && remoteEntry.key === windowKey ? remoteEntry.points : [];
        const baseWindow = remoteWindow.length >= 2 ? remoteWindow : windowedRaw;
        const mergedWindow = remoteWindow.length >= 2
          ? baseWindow
          : Array.from(new Map(baseWindow.map((p) => [p.ts, p])).values()).sort((a, b) => a.ts - b.ts);
        const sliced = downsampleMinMax(mergedWindow, FIXED_POINTS_PER_VIEW);
        if (sliced.length < 2) return null;
        const rawValues = sliced.map((p) => p.value);
        const yVals = transformedValues(rawValues, transformMode);
        const data = sliced
          .map((p, idx) => [p.ts, yVals[idx]] as [number, number])
          .filter((pt) => Number.isFinite(pt[1]))
          .filter((pt) => (useLogScale ? pt[1] > 0 : true));

        if (data.length < 2) return null;
        const axisSide = axisById[s.id] || defaultAxisSide(s.kind);

        return {
          name: `${s.label} (${s.source === 'Quantra Data' ? 'Quantra' : 'Mine'})`,
          type: 'line',
          smooth: true,
          showSymbol: false,
          sampling: 'none',
          progressive: 5000,
          progressiveThreshold: 8000,
          yAxisIndex: axisSide === 'left' ? 0 : 1,
          emphasis: { focus: 'series' },
          lineStyle: { width: 1.75 },
          data,
        };
      })
      .filter((s) => s !== null);
  }, [selectedSeries, maxTs, minTs, rangePreset, minDateInput, maxDateInput, transformMode, useLogScale, axisById, zoomWindow.startPct, zoomWindow.endPct, remoteWindowById]);

  useEffect(() => {
    let cancelled = false;
    const md = getMdBackendSettings();
    if (!md.enabled || !md.baseUrl) return;
    if (selectedSeries.length === 0 || maxTs === 0) return;

    const parsedMinTs = parseDateInput(minDateInput);
    const parsedMaxTs = parseDateInput(maxDateInput);
    const fallbackMin = rangePreset === 'all'
      ? Date.parse('1900-01-01T00:00:00.000Z')
      : (cutoffTimestamp(rangePreset, maxTs) ?? minTs);
    const rangeStartTs = parsedMinTs ?? fallbackMin;
    const rangeEndTs = parsedMaxTs ?? maxTs;
    const work: Array<{ id: string; startTs: number; endTs: number; key: string }> = [];

    for (const s of selectedSeries) {
      const slicedRaw = s.points.filter((p) => p.ts >= rangeStartTs && p.ts <= rangeEndTs);
      const bounds = zoomTimeBounds(slicedRaw, zoomWindow.startPct, zoomWindow.endPct);
      if (!bounds) continue;
      const windowKey = buildWindowKey(rangePreset, zoomWindow, rangeStartTs, rangeEndTs, bounds);
      if (fetchedWindowKeys[s.id] === windowKey) continue;
      // Always fetch each new zoom window once (deduped by key) so users can
      // observe backend requests while still using local downsampling for draw.
      work.push({ id: s.id, startTs: bounds.startTs, endTs: bounds.endTs, key: windowKey });
    }
    if (work.length === 0) return;

    const run = async () => {
      setLoadingRemote(true);
      const nextWindows: Record<string, RemoteWindowCacheEntry> = {};
      const nextKeys: Record<string, string> = {};

      await Promise.all(
        work.map(async (w) => {
          try {
            const requestStartTs = isFullZoom(zoomWindow) ? rangeStartTs : w.startTs;
            const requestEndTs = isFullZoom(zoomWindow) ? rangeEndTs : w.endTs;
            const startIso = new Date(requestStartTs).toISOString();
            const endIso = new Date(requestEndTs).toISOString();
            const response = await fetch(
              `${md.baseUrl.replace(/\/$/, '')}/series/${encodeURIComponent(w.id)}?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}&limit=50000&max_points=${FIXED_POINTS_PER_VIEW}`
            );
            if (!response.ok) return;
            const payload = (await response.json()) as Array<{ as_of: string; value: number }>;
            const pts = payload
              .map((p) => ({ ts: Date.parse(p.as_of), value: p.value }))
              .filter((p) => Number.isFinite(p.ts) && Number.isFinite(p.value))
              .sort((a, b) => a.ts - b.ts);
            if (pts.length >= 2) {
              nextWindows[w.id] = { key: w.key, points: pts };
            }
            nextKeys[w.id] = w.key;
          } catch {
            nextKeys[w.id] = w.key;
          }
        })
      );

      if (cancelled) return;
      setRemoteWindowById((prev) => ({ ...prev, ...nextWindows }));
      setFetchedWindowKeys((prev) => ({ ...prev, ...nextKeys }));
      setLoadingRemote(false);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedSeries, maxTs, minTs, rangePreset, minDateInput, maxDateInput, zoomWindow.startPct, zoomWindow.endPct, fetchedWindowKeys]);

  const option = useMemo(() => {
    const yLabel =
      transformMode === 'raw'
        ? 'Raw value'
        : transformMode === 'rebase100'
          ? 'Rebased (100=start)'
          : transformMode === 'pctChange'
            ? '% change'
            : 'Z-score';

    return {
      animation: true,
      backgroundColor: '#0b1220',
      textStyle: { color: '#d4d4d8' },
      color: [
        '#1d4ed8',
        '#d97706',
        '#059669',
        '#7c3aed',
        '#dc2626',
        '#0891b2',
        '#4338ca',
        '#65a30d',
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: 'rgba(10, 10, 10, 0.9)',
        borderColor: '#3f3f46',
        textStyle: { color: '#f4f4f5' },
        valueFormatter: (value: number) => (Number.isFinite(value) ? value.toFixed(6) : 'n/a'),
      },
      legend: {
        type: 'scroll',
        top: 0,
        textStyle: { color: '#d4d4d8' },
      },
      grid: { left: 60, right: 60, top: 48, bottom: 72 },
      xAxis: {
        type: 'time',
        axisLine: { lineStyle: { color: '#52525b' } },
        axisLabel: { color: '#d4d4d8' },
        splitLine: { lineStyle: { color: '#27272a' } },
      },
      yAxis: [
        {
          type: useLogScale ? 'log' : 'value',
          name: `${yLabel} (L)`,
          nameTextStyle: { color: '#a1a1aa' },
          axisLine: { lineStyle: { color: '#52525b' } },
          axisLabel: { color: '#d4d4d8' },
          splitLine: { lineStyle: { color: '#27272a' } },
          scale: true,
        },
        {
          type: useLogScale ? 'log' : 'value',
          name: `${yLabel} (R)`,
          nameTextStyle: { color: '#a1a1aa' },
          axisLine: { lineStyle: { color: '#52525b' } },
          axisLabel: { color: '#d4d4d8' },
          splitLine: { show: false },
          scale: true,
        },
      ],
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, filterMode: 'none', start: zoomWindow.startPct, end: zoomWindow.endPct },
        { type: 'slider', xAxisIndex: 0, height: 24, bottom: 20, filterMode: 'none', start: zoomWindow.startPct, end: zoomWindow.endPct },
      ],
      series: chartSeries,
    };
  }, [chartSeries, transformMode, useLogScale, zoomWindow.startPct, zoomWindow.endPct]);

  useEffect(() => {
    setZoomWindow({ startPct: 0, endPct: 100 });
  }, [rangePreset, selectedIds.join('|')]);

  const toggleSeries = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const chartEvents = useMemo(
    () => ({
      datazoom: (event: any) => {
        const batch = Array.isArray(event?.batch) ? event.batch[0] : event;
        const start = Number(batch?.start);
        const end = Number(batch?.end);
        if (Number.isFinite(start) && Number.isFinite(end)) {
          const clampedStart = Math.max(0, Math.min(100, start));
          const clampedEnd = Math.max(0, Math.min(100, end));
          setZoomWindow({ startPct: clampedStart, endPct: Math.max(clampedStart + 0.1, clampedEnd) });
        }
      },
    }),
    []
  );

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />
      <main className="max-w-[1480px] mx-auto px-4 sm:px-6 pt-24 pb-10">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-[#0a0a0a]">Time Series Lab</h1>
          <p className="text-[#737373] mt-1">
            Smooth multi-series charting with normalization and scale controls for Quantra and user market data.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <section className="xl:col-span-4 bg-white border border-[#e5e5e5] rounded-xl p-4">
            <h2 className="text-sm font-semibold text-[#0a0a0a] mb-3">Series Selection</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search series..."
                className="px-3 py-2 text-sm border border-[#d4d4d4] rounded-lg focus:outline-none focus:border-[#8a6a2f]"
              />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as DataSourceFilter)}
                className="px-3 py-2 text-sm border border-[#d4d4d4] rounded-lg"
              >
                <option value="all">All sources</option>
                <option value="quantra">Quantra Data</option>
                <option value="my">My Data</option>
              </select>
            </div>

            <div className="max-h-[420px] overflow-auto border border-[#e5e5e5] rounded-lg">
              {filteredUniverse.length === 0 ? (
                <div className="p-3">
                  <PanelPlaceholder
                    compact
                    icon="timeSeries"
                    title="No matching series"
                    description="Adjust the filters or search for a different series."
                  />
                </div>
              ) : (
                filteredUniverse.map((s) => {
                  const checked = selectedIds.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className="flex items-start gap-3 p-3 border-b border-[#f5f5f5] hover:bg-[#fafafa] cursor-pointer"
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleSeries(s.id)} className="mt-1" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#0a0a0a] truncate">{s.label}</div>
                        <div className="text-xs text-[#737373] truncate">{s.id}</div>
                        <div className="text-[11px] text-[#a3a3a3] mt-0.5">
                          {s.kind} {s.currency ? `- ${s.currency}` : ''} - {s.source}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {selectedSeries.length > 0 && (
              <div className="mt-3 border border-[#e5e5e5] rounded-lg p-3">
                <div className="text-xs font-medium text-[#525252] mb-2">Axis Assignment</div>
                <div className="max-h-40 overflow-auto space-y-1">
                  {selectedSeries.map((s) => {
                    const side = axisById[s.id] || defaultAxisSide(s.kind);
                    return (
                      <div key={s.id} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-[#525252] truncate">{s.label}</span>
                        <select
                          value={side}
                          onChange={(e) =>
                            setAxisById((prev) => ({ ...prev, [s.id]: e.target.value as AxisSide }))
                          }
                          className="px-2 py-1 text-xs border border-[#d4d4d4] rounded-lg"
                        >
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <section className="xl:col-span-8 bg-white border border-[#e5e5e5] rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select
                value={transformMode}
                onChange={(e) => setTransformMode(e.target.value as TransformMode)}
                className="px-3 py-2 text-sm border border-[#d4d4d4] rounded-lg"
              >
                <option value="raw">Raw</option>
                <option value="rebase100">Rebased to 100</option>
                <option value="pctChange">% Change</option>
                <option value="zscore">Z-Score</option>
              </select>
              <select
                value={rangePreset}
                onChange={(e) => setRangePreset(e.target.value as TimeRangePreset)}
                className="px-3 py-2 text-sm border border-[#d4d4d4] rounded-lg"
              >
                <option value="1m">1M</option>
                <option value="3m">3M</option>
                <option value="6m">6M</option>
                <option value="ytd">YTD</option>
                <option value="1y">1Y</option>
                <option value="3y">3Y</option>
                <option value="5y">5Y</option>
                <option value="all">All</option>
              </select>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-[#737373]">Min</label>
                <input
                  type="date"
                  value={minDateInput}
                  onChange={(e) => setMinDateInput(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-[#d4d4d4] rounded-lg"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-[#737373]">Max</label>
                <input
                  type="date"
                  value={maxDateInput}
                  onChange={(e) => setMaxDateInput(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-[#d4d4d4] rounded-lg"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#525252] px-2">
                <input type="checkbox" checked={useLogScale} onChange={(e) => setUseLogScale(e.target.checked)} />
                Log scale
              </label>
              <span className="ml-auto text-xs text-[#a3a3a3]">
                Showing {chartSeries.length} plotted / {selectedSeries.length} selected series
              </span>
              {loadingRemote && (
                <span className="text-xs text-[#737373] inline-flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  Loading zoom window...
                </span>
              )}
            </div>

            {chartSeries.length === 0 ? (
              <div className="h-[520px] border border-[#e5e5e5] rounded-lg flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                  <PanelPlaceholder
                    compact
                    icon="timeSeries"
                    title="No chartable series selected"
                    description="Select at least one series with enough data points."
                  />
                </div>
              </div>
            ) : (
              <ReactECharts
                option={option}
                onEvents={chartEvents}
                style={{ height: 520, width: '100%' }}
                notMerge
                lazyUpdate
              />
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
